import { Environments } from './environments.enum';
// eslint-disable-next-line @nx/enforce-module-boundaries
import packageInfo from './../../../../../../package.json';
import { FeatureConfig } from './environment.tokens';

export const APP_VERSION = `vaki-web@${packageInfo?.version || 'undefined'}`;

export interface Environment {
  env: Environments;
  appVersion: string;
  useEmulators: boolean;
  production: boolean;
  publicApiUrl: string;
  adminApiKey?: string;
  firebaseConfig: {
    apiKey: string;
    authDomain: string;
    databaseURL: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    measurementId: string;
  };
  paypal_mx: {
    client_id: string;
  };
  mercadopago: {
    key: string;
    notification_url: string;
  };
  stripe_key: string;
  stripe_key_mx: string;
  hostUrl: string;
  /** The stablecoin donation landing (cripto.vaki.co). Per environment because it opens intents against that environment's API. */
  cryptoLandingUrl: string;
  apiVakiURL: string;
  /**
   * Base URL of `functions/src/ai/lolaCreateVaki.ts` (Notion
   * 3e5c94b6-2cc7-81b9-816a-f6b1af37ad34, Build · Rebanada 3) — a standalone
   * `onRequest` 2nd-gen function, NOT a route under `apiVakiURL`/`api`
   * (streaming needs its own timeout/minInstances config). Same
   * `https://{region}-{projectId}.cloudfunctions.net/{name}` shape as
   * `mercadopago.notification_url` below. TODO(rebanada 3): confirm this
   * URL once the function is actually deployed — nothing here has verified
   * it against a live deploy yet.
   */
  lolaCreateVakiUrl: string;
  adminUid: string;
  adminEmail: string;
  froalaKey: string;
  intercom: {
    appId: string;
  };
  algolia: {
    appId: string;
    apiKey: string;
    indexes: {
      vakis: string;
    };
  };
  googleTagManager: {
    id: string;
    auth: string;
    env: string;
  };
  featureFlagsDefault: FeatureConfig;
  recaptcha: {
    siteKey: string;
    appCheckDebugToken?: string;
  };
  privyAppId: string;
  platformHash: string;
  campaignInfoFactoryAddress: string;
  pledgeTokenAddress: string;
  chainId: number;
  rpcUrl: string;
  platformAdminWalletAddress: string;
  siigo: {
    fv_id: number;
    seller_id: number;
    payment_method: number;
    product_id: string;
  };
  whatsapp: {
    accountSid: string;
    authToken: string;
    phoneNumber: string;
    webhookUrl: string;
  };
  /**
   * Vaki's own WhatsApp Business number connected to Intercom — the ONE
   * Vaki number every WhatsApp conversation in Intercom shares (verified
   * live 2026-09-29, see docs/integrations/whatsapp-number-binding.md).
   * Distinct from `whatsapp.phoneNumber` above (Twilio, a different,
   * unrelated integration). Shown to a person as the number to send their
   * linking code to — a config value, never hardcoded in a component, so
   * it can be corrected in one place if it changes.
   */
  whatsappLinkPhoneNumber: string;
  useBigQueryForVakis?: boolean;
  /**
   * Sentry error tracking (vaki-web only). `dsn` is intentionally NOT a
   * secret — Sentry's browser DSN is public by design and ships in the
   * bundle; the real secret is the CI auth token used to upload source
   * maps, which never lives here. `enabled` is the build-time default:
   * true for dev/staging, false for production until the PII-scrubbing
   * criterion passes and Nic signs Sentry's DPA (see
   * docs/operations/error-tracking-runbook.md). Once flipped on, the
   * actual on/off switch at runtime is the `error_tracking_kill_switch`
   * Remote Config boolean — same rail `checkout_direction_c_rollout_percentage`
   * uses — so killing or reviving capture never needs a deploy.
   */
  sentry: {
    enabled: boolean;
    dsn: string;
    /** 0-1. Local fallback used until Remote Config's
     *  `error_tracking_sample_rate_percent` is read (or on any RC error). */
    sampleRate: number;
  };
}
